import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, downloadFile } from '../../api/client'
import type { OrdrestyringSettings } from '../../api/types'
import { Card, ErrorBox, Loading } from '../../components/ui'
import { formatDateTime } from '../../lib/format'

/**
 * Nøglen til Ordrestyrings API (beslutning 137). Kun Superadmin.
 *
 * Nøglen gemmes krypteret på serveren og kan ikke hentes frem igen - feltet står derfor altid tomt. "Prøv forbindelsen"
 * henter Ordrestyrings skema og ændrer intet hos Ordrestyring. "Hent skemaet" giver skemaet som fil, så det kan lægges
 * i docs/ og slås op i (docs/ORDRESTYRING-API.md).
 */
export function OrdrestyringPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['ordrestyring-settings'],
    queryFn: () => api.get<OrdrestyringSettings>('/api/ordrestyring/settings')
  })
  const [apiKey, setApiKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  async function run(action: () => Promise<unknown>, done?: string) {
    setBusy(true)
    setMessage(null)
    try {
      await action()
      await queryClient.invalidateQueries({ queryKey: ['ordrestyring-settings'] })
      if (done) setMessage({ ok: true, text: done })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Det gik ikke.' })
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    await run(async () => {
      await api.put('/api/ordrestyring/settings', { apiKey })
      setApiKey('')
      // Prøv den nye nøgle med det samme - så ved man, om den virker.
      await api.post('/api/ordrestyring/test', {})
    }, 'Nøglen er gemt og prøvet - se resultatet herunder.')
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card title="Ordrestyrings API">
        <p className="small muted" style={{ marginTop: 0 }}>
          Nøglen giver Overblik adgang til jeres data i Ordrestyring. Den fås hos Ordrestyring (api@ordrestyring.dk) og
          gemmes krypteret her - den kan ikke hentes frem igen og vises aldrig. Indsæt den aldrig i en mail eller en chat.
        </p>

        <div className="stack" style={{ gap: 14 }}>
          <div className="small">
            Status:{' '}
            {data?.hasApiKey ? <strong>Der er gemt en nøgle</strong> : <strong>Ingen nøgle</strong>}
          </div>

          <label className="stack" style={{ gap: 4 }}>
            <span className="small">{data?.hasApiKey ? 'Ny nøgle (erstatter den gemte)' : 'API-nøgle'}</span>
            <input
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="Indsæt nøglen fra Ordrestyring"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </label>

          {message && (
            <div className={message.ok ? 'small' : 'error-box'} style={{ color: message.ok ? 'var(--success)' : undefined }}>
              {message.text}
            </div>
          )}

          <div className="btn-group">
            <button className="btn btn--primary" disabled={busy || !apiKey.trim()} onClick={() => void save()}>
              Gem nøglen
            </button>
            {data?.hasApiKey && (
              <button
                className="btn btn--danger"
                disabled={busy}
                onClick={() => {
                  if (window.confirm('Fjern nøglen til Ordrestyring?')) {
                    void run(() => api.delete('/api/ordrestyring/settings/api-key'), 'Nøglen er fjernet.')
                  }
                }}
              >
                Fjern nøglen
              </button>
            )}
          </div>
        </div>
      </Card>

      {data?.hasApiKey && (
        <Card title="Forbindelsen">
          <div className="stack" style={{ gap: 10 }}>
            {data.checkedAt ? (
              <div className="small" style={{ padding: 10, borderRadius: 8, background: data.checkOk ? '#F0FDF4' : '#FEF2F2' }}>
                {data.checkOk ? '✓ ' : '⛔ '}
                {data.checkMessage}
                <div className="tiny muted">Prøvet {formatDateTime(data.checkedAt)}</div>
              </div>
            ) : (
              <div className="small muted">Nøglen er ikke prøvet endnu.</div>
            )}
            <div className="btn-group">
              <button className="btn" disabled={busy} onClick={() => void run(() => api.post('/api/ordrestyring/test', {}))}>
                Prøv forbindelsen
              </button>
              <button
                className="btn"
                disabled={busy}
                onClick={() => void run(() => downloadFile('/api/ordrestyring/schema', 'ordrestyring-schema.json'))}
              >
                ⬇ Hent skemaet
              </button>
            </div>
            <p className="tiny muted" style={{ margin: 0 }}>
              "Prøv forbindelsen" ændrer intet hos Ordrestyring. "Hent skemaet" giver en fil med alle Ordrestyrings kald og
              felter - læg den i projektets docs-mappe, så den kan slås op i, når vi bygger videre.
            </p>
          </div>
        </Card>
      )}
    </div>
  )
}
